import {makeStyles} from "@material-ui/core"

/**
 * The annotation surface.
 *
 * Laid out as overview-on-top, work-area-below, because the two answer
 * different questions: "which beat am I on" is a glance, "where exactly does
 * this wave start" is the job. An earlier arrangement put the whole ten-second
 * strip in the working position and a magnifier 600 px below it, which meant the
 * technician's eyes crossed most of the screen on every single correction. The
 * work area here IS the magnified view, so they never leave it.
 */
export default makeStyles(theme => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: theme.palette.background.paper,
    },

    /**
     * The whole record, compressed. Navigation and context only — nothing is
     * placed at this scale, because at paper speed one pixel is ~10 ms and the
     * label needs 2.
     */
    overview: {
        position: "relative",
        flex: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        background: theme.palette.background.default,
    },

    overview_hint: {
        position: "absolute",
        top: 4,
        right: 8,
        font: "500 10.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.text.secondary,
        pointerEvents: "none",
    },

    /** Where the work happens. Scrolls only if the leads do not fit. */
    work: {
        position: "relative",
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "auto",
    },

    /** As wide as the paper, so the pane scrolls the whole record. */
    paper: {
        position: "relative",
    },

    /** The size of the pane, held over the visible part of the paper. */
    work_canvas: {
        display: "block",
        position: "sticky",
        left: 0,
    },

    /**
     * The readout, pinned to the selected boundary line rather than parked in a
     * far sidebar. The number a technician needs is the one belonging to the
     * line under their eyes.
     */
    tag: {
        position: "absolute",
        transform: "translateX(-50%)",
        padding: "2px 7px",
        borderRadius: 4,
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        font: "600 11px/1.35 -apple-system, Segoe UI, Roboto, sans-serif",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        pointerEvents: "none",
        zIndex: 3,
        boxShadow: theme.shadows[2],
    },

    tag_sub: {
        display: "block",
        fontWeight: 500,
        opacity: 0.85,
    },

    /**
     * The six boundaries, laid out left to right in the order they occur.
     *
     * This is the control surface. Without it the whole screen was invisible
     * keyboard shortcuts: nothing said what the six marks were, which had been
     * set, or that a start means the earliest lead and an end the latest. Every
     * cell states its own rule, all the time, because that rule is the thing a
     * technician is most likely to forget halfway through a record.
     */
    bar: {
        flex: "none",
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 1,
        background: theme.palette.divider,
        borderTop: `1px solid ${theme.palette.divider}`,
    },

    /** A group: the select button, then the nudge buttons. */
    cell: {
        background: theme.palette.background.paper,
        padding: "6px 9px 7px",
        borderTop: "2px solid transparent",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        "&:hover": {
            background: theme.palette.action.hover,
        },
    },

    /** The part of a cell that selects its boundary — a real button, unstyled. */
    cell_select: {
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        width: "100%",
        "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
    },

    cell__selected: {
        borderTopColor: theme.palette.primary.main,
        background: theme.palette.action.selected,
    },

    cell__absent: {
        opacity: 0.62,
    },

    /** A boundary that breaks the order: red edge, kept at full strength. */
    cell__order: {
        borderTopColor: "#dc2626",
        opacity: 1,
    },

    cell_name: {
        font: "600 12px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },

    /** The envelope rule, on every cell, always. */
    cell_rule: {
        font: "500 10.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },

    cell_value: {
        font: "600 13px -apple-system, Segoe UI, Roboto, sans-serif",
        fontVariantNumeric: "tabular-nums",
        color: theme.palette.primary.main,
        marginTop: 2,
    },

    cell_sub: {
        font: "11px -apple-system, Segoe UI, Roboto, sans-serif",
        fontVariantNumeric: "tabular-nums",
        color: theme.palette.text.secondary,
    },

    cell_row: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        marginTop: 3,
    },

    nudge: {
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        borderRadius: 3,
        cursor: "pointer",
        font: "600 11px ui-monospace, Consolas, monospace",
        lineHeight: "16px",
        padding: "0 6px",
        color: theme.palette.text.primary,
        "&:hover": {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
    },

    /** Why a mark breaks the order. Red: the beat cannot be confirmed like this. */
    cell_order: {
        font: "600 10.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: "#dc2626",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginTop: 2,
    },

    /** A mark that does not contain some lead. Amber: a correction, not an error. */
    cell_miss: {
        font: "600 10.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: "#b45309",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginTop: 2,
    },

    cell_flag: {
        font: "600 10px -apple-system, Segoe UI, Roboto, sans-serif",
        color: "#b45309",
        marginLeft: "auto",
    },

    /** Visible equivalents of the view keys, so nothing is keyboard-only. */
    controls: {
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderTop: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.default,
        flexWrap: "wrap",
    },

    ctl: {
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        borderRadius: 4,
        cursor: "pointer",
        font: "600 11.5px -apple-system, Segoe UI, Roboto, sans-serif",
        padding: "3px 9px",
        color: theme.palette.text.primary,
        "&:hover": {
            borderColor: theme.palette.primary.main,
        },
    },

    ctl__on: {
        background: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },


    save_ok: {
        font: "600 11.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: "#15803d",
        whiteSpace: "nowrap",
    },

    /** Loud on purpose: the alternative is believing the work is safe. */
    save_bad: {
        font: "600 11.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.error.main,
        whiteSpace: "nowrap",
    },


    ctl_group: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        paddingLeft: 10,
        marginLeft: 2,
        borderLeft: `1px solid ${theme.palette.divider}`,
    },

    ctl_label: {
        font: "11.5px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        padding: "0 2px",
    },

    /** Status bar: what beat, what class, what the keys do. */
    foot: {
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "5px 12px",
        borderTop: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.default,
        font: "12px -apple-system, Segoe UI, Roboto, sans-serif",
        fontVariantNumeric: "tabular-nums",
        flexWrap: "wrap",
    },

    foot_group: {
        display: "flex",
        alignItems: "center",
        gap: 6,
    },

    foot_label: {
        color: theme.palette.text.secondary,
    },

    foot_value: {
        fontWeight: 600,
    },

    foot_flag: {
        color: theme.palette.error.main,
    },

    /** The per-lead reference legend, in the colour of its ticks on the trace. */
    foot_ref: {
        color: "rgb(194, 65, 12)",
    },

    foot_spacer: {
        marginLeft: "auto",
    },

    chip: {
        padding: "1px 7px",
        borderRadius: 10,
        font: "600 11px -apple-system, Segoe UI, Roboto, sans-serif",
    },

    chip__auto: {
        background: theme.palette.action.hover,
        color: theme.palette.text.secondary,
    },

    chip__confirmed: {
        background: "rgba(21, 128, 61, .14)",
        color: "#15803d",
    },

    chip__corrected: {
        background: "rgba(29, 78, 216, .14)",
        color: theme.palette.primary.main,
    },

    keys: {
        display: "flex",
        gap: 10,
        color: theme.palette.text.secondary,
        fontSize: 11.5,
        flexWrap: "wrap",
    },

    kbd: {
        font: "600 10.5px ui-monospace, Consolas, monospace",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderBottomWidth: 2,
        borderRadius: 3,
        padding: "0 4px",
        marginRight: 3,
    },

    empty: {
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        color: theme.palette.text.secondary,
        padding: 24,
        textAlign: "center",
    },

    empty_title: {
        font: "600 14px -apple-system, Segoe UI, Roboto, sans-serif",
        color: theme.palette.text.primary,
    },
}));
